import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { getClients } from '../../lib/api'
import ScrollReveal from '../ui/ScrollReveal'
import Parallax from '../ui/Parallax'
import ClientLogo from '../ui/ClientLogo'
import type { Client } from '../../types'

function ClientCard({ client, index }: { client: Client; index: number }) {
  return (
    <ScrollReveal delay={Math.min(index * 0.04, 0.3)}>
      <div className="group relative p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-500 hover:bg-white/[0.06] hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20 h-full flex flex-col items-center justify-center text-center gap-4 min-h-[160px]">
        <ClientLogo name={client.name} logo={client.logo} gradient={client.gradient} index={index} size="lg" />
        <div>
          <h3 className="text-sm font-bold text-white/90 group-hover:text-white transition-colors duration-300 leading-snug">
            {client.name}
          </h3>
          <p className="mt-1 text-[11px] text-gray-500 group-hover:text-gray-400 transition-colors duration-300 font-medium tracking-wide uppercase">
            {client.category}
          </p>
        </div>
      </div>
    </ScrollReveal>
  )
}

export default function ClientCarousel() {
  const [clients, setClients] = useState<Client[]>([])

  useEffect(() => {
    getClients().then(setClients)
  }, [])

  if (clients.length === 0) return null

  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-premium-950/10 to-gray-950" />
      <Parallax speed={0.15} className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[400px]">
        <div className="w-full h-full rounded-full bg-gradient-to-br from-electric-blue/[0.04] via-electric-purple/[0.04] to-transparent blur-[120px]" />
      </Parallax>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span className="inline-block mb-3 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-electric-blue bg-electric-blue/[0.07] rounded-full border border-electric-blue/[0.15]">
            Our Clients
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Trusted by{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              Local Businesses
            </span>
          </h2>
          <p className="mt-3 text-sm text-gray-500 max-w-xl mx-auto">
            Proud to serve Nagercoil&rsquo;s finest — from jewelry showrooms to
            hospitals, schools, and beyond.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {clients.map((client, i) => (
            <ClientCard key={client.id} client={client} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
